import React, { FunctionComponent, ReactNode } from "react";
import { IonLabel, IonList, IonListHeader, IonNote, IonGrid, IonRow, IonCol, IonIcon } from "@ionic/react";
import alphanumericIcon from '../../../icons/alphanumeric.svg';
import { chevronForward, cloudOffline } from "ionicons/icons";
import { useHistory } from "react-router-dom";
import { RouteBuilder } from "../../router/constantes/routes.const";
import { useOrganizationCode } from "../../router/hooks/use-organization-code.hook";
import { useLocale } from "../../router/hooks/use-locale.hook";

export const EditIonCardList: FunctionComponent<{
  title: string;
  subtitle?: ReactNode;
  list: {
    id: string;
    name: string;
    address?: string;
    icon?: string,
    numberOfAnnotations: number,
    isGraphic?: boolean,
    isExported: boolean,
    numberOfExportedBuildings: number,
    numberOfBuildings: number,
    isOffline?: boolean,
  }[];
  itemChild?: (item: any) => ReactNode;
  getRouterLink: (id: string) => string;
  secondSubtitle?: string;
  secondList?: {
    id: string;
    name: string;
    icon?: string,
    numberOfAnnotations: number,
    numberOfAnnotationsOnArea: number,
    isExported: boolean,
    isOffline?: boolean,
  }[];
  secondItemChild?: (item: any) => ReactNode;
  getSecondRouterLink?: (id: string) => string;
  isBuildingList: boolean;
  showAnnotationsButton?: boolean;
  siteId?: string;
  numberOfAnnotations?: number;
  numberOfAnnotationsOnArea?: number;
}> = ({
  title,
  subtitle,
  list,
  itemChild,
  getRouterLink,
  secondSubtitle,
  secondList,
  secondItemChild,
  getSecondRouterLink,
  isBuildingList,
  showAnnotationsButton,
  siteId,
  numberOfAnnotations,
  numberOfAnnotationsOnArea
}) => {
  const history = useHistory();
  const organizationCode = useOrganizationCode();
  const locale = useLocale();

  const handleClick = (id: string, isBuildingList: boolean, isGraphic?: boolean, address?: string) => {
    if (!(isBuildingList && !isGraphic)) {
      // Utiliser l'adresse de l'item ou le subtitle (adresse parente) si disponible
      const addressToPass = address || (typeof subtitle === 'string' ? subtitle : undefined);
      
      // Stocker l'adresse dans sessionStorage pour contourner le problème de perte de state avec Ionic
      if (addressToPass) {
        sessionStorage.setItem('buildingAddress', addressToPass);
      }
      
      history.push(getRouterLink(id), { address: addressToPass });
    }
  };

  const handleAnnotationsClick = () => {
    if (siteId) {
      history.push(RouteBuilder.siteAnnotations(siteId, locale, organizationCode));
    } else {
      history.push(RouteBuilder.annotations(locale, organizationCode));
    }
  };

  return (
    <IonList className={title ? "ion-padding-16 ion-padding-lg-24" : ""} style={{ paddingBottom: secondList && secondList.length > 0 ? '70px' : '60px' }}>
      {showAnnotationsButton && siteId && (numberOfAnnotations || 0) + (numberOfAnnotationsOnArea || 0) >= 1 && (
        <div style={{ padding: '0 8px 16px 8px', display: 'flex', justifyContent: 'flex-start' }}>
          <div
            onClick={handleAnnotationsClick}
            style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '6px',
              padding: '12px 16px',
              margin: '8px',
              boxShadow: '1px 1px 3px 0px var(--elevation-xs, rgba(0, 0, 0, 0.20))',
              cursor: 'pointer',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: 'calc(25% - 16px)',
              minWidth: '250px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: '#FF8402'
              }}></span>
              <IonLabel style={{ 
                fontSize: 12, 
                color: '#4B4D56',
                fontFamily: 'Open Sans, sans-serif',
                fontWeight: '500'
              }}>
                Voir les annotations ({(numberOfAnnotations || 0) + (numberOfAnnotationsOnArea || 0)})
              </IonLabel>
            </div>
            <IonIcon 
              icon={chevronForward} 
              style={{
                color: '#4B4D56',
                fontSize: '16px'
              }}
            />
          </div>
        </div>
      )}
      {subtitle && (
        <IonLabel style={{ 
          paddingLeft: 20,
          fontSize: '10px',
          display: 'block',
          fontFamily: 'Open Sans, sans-serif',
          fontWeight: 400,
          lineHeight: '1',
          letterSpacing: '0.5px',
          color: '#165BE6',
          marginBottom: '5px',
          marginTop: '16px'
        }} color={ 'tertiary' }>
          {subtitle}
        </IonLabel>
      )}
      {title && (
        <IonListHeader>
          <IonLabel style={{ marginTop: 0, fontSize: 16 }}>{title}</IonLabel>
        </IonListHeader>
      )}
      <IonGrid>
        <IonRow>
          {list.map((item) => (
            <IonCol size="12" sizeLg="3" key={item.id}>
              <div 
                onClick={() => handleClick(item.id, isBuildingList, item.isGraphic, item.address)}
                style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '6px',
                  padding: itemChild ? '0' : '6px 8px 8px 8px',
                  margin: '8px',
                  boxShadow: '1px 1px 3px 0px var(--elevation-xs, rgba(0, 0, 0, 0.20))',
                  cursor: 'pointer',
                  position: 'relative',
                  height: itemChild ? '85px' : 'auto'
                }}
              >
                {!(isBuildingList && !item.isGraphic) && (
                  <IonIcon 
                    icon={chevronForward} 
                    style={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#4B4D56'
                    }}
                  />
                )}
                {itemChild ? (
                  itemChild(item)
                ) : (
                  <>
                    <div style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      position: 'relative'
                    }}>
                      <div style={{ position: 'relative', flexShrink: 0, margin: '0 10px' }}>
                        <img src={ item.icon } alt="" style={{ 
                          height: 28, 
                          width: 28
                        }}/>
                        {item.isOffline && (
                          <IonIcon 
                            icon={cloudOffline} 
                            style={{
                              position: 'absolute',
                              bottom: '-2px',
                              right: '-2px',
                              fontSize: '14px',
                              color: 'var(--ion-color-secondary)',
                              backgroundColor: '#FFFFFF',
                              borderRadius: '50%',
                              padding: '1px'
                            }}
                          />
                        )}
                      </div>
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        flex: 1
                      }}>
                        { item.address && (
                          <IonLabel style={{ 
                            fontSize: '10px',
                            display: 'inline-block',
                            verticalAlign: 'middle',
                            fontFamily: 'Open Sans, sans-serif',
                            fontWeight: 400,
                            lineHeight: '1',
                            letterSpacing: '0.5px',
                            color: '#165BE6',
                            marginBottom: '2px'
                          }} color={ 'tertiary' }>
                            { item.address }
                          </IonLabel>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          { !item.isGraphic && (
                            <img src={ alphanumericIcon }
                                 style={{
                                   width: '18px',
                                   height: '18px',
                                   marginRight: '5px',
                                   display: 'inline-block',
                                   verticalAlign: 'middle'
                                 }}
                                 alt=""
                            />
                          )}
                          { item.numberOfAnnotations > 0 && (
                            <IonLabel
                              style={{
                                fontSize: '0.4rem',
                                color: '#FDFDFD',
                                backgroundColor: '#FF8402',
                                borderRadius: '12px',
                                minWidth: '15px',
                                minHeight: '15px',
                                width: '15px',
                                height: '15px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '2px 2px 2px 2px',
                                gap: '10px',
                                verticalAlign: 'middle',
                                fontWeight: '500',
                                marginRight: '5px',
                              }}
                            >
                              {item.numberOfAnnotations > 99 ? '99+' : item.numberOfAnnotations}
                            </IonLabel>
                          )}
                          <IonLabel style={ { fontSize: 12, display: 'inline-block', verticalAlign: 'middle' } }>{ item.name }</IonLabel>
                        </div>
                        
                        { item.isExported && (
                          <IonLabel
                            style={{
                              fontSize: '0.7rem',
                              color: '#CA6700',
                              backgroundColor: '#FAECE3',
                              borderRadius: '4px',
                              height: '20px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: '2px 8px 2px 8px',
                              gap: '6px',
                              verticalAlign: 'middle',
                              fontWeight: '500',
                              marginTop: '4px',
                              width: 'fit-content'
                            }}
                          >
                            { 'Bâtiment en mise à jour' }
                          </IonLabel>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </IonCol>
          ))}
        </IonRow>
      </IonGrid>
      { secondList && secondList.length > 0 && (
        <>
          <IonNote style={{ paddingLeft: 20, fontSize: 14 }}>{secondSubtitle}</IonNote>
          <IonGrid style={{ marginBottom: '70px', paddingBottom: '5px' }}>
            <IonRow>
              {secondList.map((item) => (
                <IonCol size="12" sizeLg="3" key={item.id}>
                  <div 
                    onClick={() => getSecondRouterLink && history.push(getSecondRouterLink(item.id))}
                    style={{
                      backgroundColor: '#FFFFFF',
                      borderRadius: '6px',
                      padding: '6px 8px 8px 8px',
                      margin: '8px',
                      boxShadow: '1px 1px 3px 0px var(--elevation-xs, rgba(0, 0, 0, 0.20))',
                      cursor: 'pointer',
                      position: 'relative'
                    }}
                  >
                    <IonIcon 
                      icon={chevronForward} 
                      style={{
                        position: 'absolute',
                        right: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#4B4D56'
                      }}
                    />
                    {secondItemChild ? (
                      secondItemChild(item)
                    ) : (
                      <>
                        <div style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          position: 'relative'
                        }}>
                          <div style={{ position: 'relative', flexShrink: 0, margin: '0 10px' }}>
                            <img src={ item.icon } alt="" style={{ 
                              height: 28, 
                              width: 28
                            }}/>
                            {item.isOffline && (
                              <IonIcon 
                                icon={cloudOffline} 
                                style={{
                                  position: 'absolute',
                                  bottom: '-2px',
                                  right: '-2px',
                                  fontSize: '14px',
                                  color: 'var(--ion-color-secondary)',
                                  backgroundColor: '#FFFFFF',
                                  borderRadius: '50%',
                                  padding: '1px'
                                }}
                              />
                            )}
                          </div>
                          <div style={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            flex: 1
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              { item.numberOfAnnotationsOnArea > 0 && (
                                <IonLabel
                                  style={{
                                    fontSize: '0.4rem',
                                    color: '#FDFDFD',
                                    backgroundColor: '#FF8402',
                                    borderRadius: '12px',
                                    maxWidth: '15px',
                                    height: '12px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '2px 2px 2px 2px',
                                    gap: '10px',
                                    verticalAlign: 'middle',
                                    fontWeight: '500',
                                    marginRight: '5px',
                                  }}
                                >
                                  {item.numberOfAnnotationsOnArea > 99 ? '99+' : item.numberOfAnnotationsOnArea}
                                </IonLabel>
                              )}
                              <IonLabel style={ { fontSize: 12, display: 'inline-block', verticalAlign: 'middle' } }>{ item.name }</IonLabel>
                            </div>
                            
                            { item.isExported && (
                              <IonLabel
                                style={{
                                  fontSize: '0.7rem',
                                  color: '#CA6700',
                                  backgroundColor: '#FAECE3',
                                  borderRadius: '4px',
                                  height: '20px',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: '2px 8px 2px 8px',
                                  gap: '6px',
                                  verticalAlign: 'middle',
                                  fontWeight: '500',
                                  marginTop: '4px',
                                  width: 'fit-content'
                                }}
                              >
                                { 'Site en mise à jour' }
                              </IonLabel>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </IonCol>
              ))}
            </IonRow>
          </IonGrid>
        </>
      )}
    </IonList>
  );
};
